import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FolderOpenOutlined, PrinterOutlined, UploadOutlined } from '@ant-design/icons';
import {
  getQzPrintReadiness,
  loadQzPrintSettings,
  reconcileQzPrintSettings,
  saveQzPrintSettings,
} from '../../services/qzPrintService';
import QzPrinterSettingsPanel from './QzPrinterSettingsPanel';
import { parsePdfPageRanges } from '../../services/qzPrintValidation';
import { usePrintQueue } from '../../context/PrintQueueContext';
import {
  deletePrintQueueBlob,
  getPrintQueueBlob,
  putPrintQueueBlob,
} from '../../services/printQueueStore';
import {
  extractShippingLabelPdfPages,
  matchShippingLabelPagesToOrders,
} from '../../services/shippingLabelPdfMatchService';
import { useAuth } from '../../context/AuthContext';
import { getDriveShareLink } from '../../services/operatorDriveService';
import { loadOperatorDriveItemBlob, formatDriveStorageCorsMessage } from '../../utils/operatorDriveShareDownload';
import { consumeShippingLabelPdfHandoff } from '../../utils/shippingLabelPdfHandoff';
import { stageMatchedShippingLabelPdfToQueue } from '../../utils/stageMatchedShippingLabelPdfToQueue';
import {
  inspectShippingLabelPdfGeometry,
  shippingLabelPageSizeMatchesTarget,
  SHIPPING_LABEL_TARGET_HEIGHT_MM,
  SHIPPING_LABEL_TARGET_WIDTH_MM,
} from '../../utils/shippingLabelHeuristic';
import OperatorDrivePicker from '../operatorDrive/OperatorDrivePicker';
import './ShippingLabelPdfPanel.css';

const MAX_PDF_BYTES = 50 * 1024 * 1024;
const TARGET_WIDTH_MM = SHIPPING_LABEL_TARGET_WIDTH_MM;
const TARGET_HEIGHT_MM = SHIPPING_LABEL_TARGET_HEIGHT_MM;

const getOrderAssignmentLabel = (order) => {
  const key = String(
    order?.source_id || order?.sourceId || order?.orderId || order?.id || '',
  ).replace(/^#/, '');
  const shipping = order?.shipping || order?.shippingAddress || order?.shipping_address || {};
  const customer = order?.customer || order?.billing || {};
  const name =
    shipping.name ||
    `${shipping.first_name || shipping.firstName || ''} ${
      shipping.last_name || shipping.lastName || ''
    }`.trim() ||
    customer.name ||
    `${customer.first_name || customer.firstName || ''} ${
      customer.last_name || customer.lastName || ''
    }`.trim();
  const company =
    shipping.company || customer.company || order?.customerCompany || order?.companyName;
  const postcode =
    shipping.postal_code ||
    shipping.postalCode ||
    shipping.postCode ||
    order?.shippingPostalCode ||
    customer.postal_code ||
    customer.postalCode;
  return [`Order ${key}`, name, company, postcode].filter(Boolean).join(' · ');
};

async function inspectPdf(blob, fileName) {
  if (!blob?.size) throw new Error('The selected PDF is empty.');
  if (blob.size > MAX_PDF_BYTES) throw new Error('PDF exceeds the 50 MB safety limit.');
  const geometry = await inspectShippingLabelPdfGeometry(blob);
  if (!geometry.ok) {
    throw new Error(
      geometry.reason === 'no-pages'
        ? 'The PDF contains no printable pages.'
        : 'Could not read that PDF.',
    );
  }
  const { PDFDocument } = await import('pdf-lib');
  const document = await PDFDocument.load(await blob.arrayBuffer(), {
    ignoreEncryption: false,
    updateMetadata: false,
  });
  const pages = document.getPages();
  const dimensions = pages.map((page) => {
    const size = page.getSize();
    return {
      widthMm: (size.width / 72) * 25.4,
      heightMm: (size.height / 72) * 25.4,
    };
  });
  const first = dimensions[0];
  const sameSize = dimensions.every(
    (size) =>
      Math.abs(size.widthMm - first.widthMm) < 0.5 &&
      Math.abs(size.heightMm - first.heightMm) < 0.5,
  );
  return {
    name: fileName || 'shipping-labels.pdf',
    bytes: blob.size,
    pageCount: pages.length,
    widthMm: first.widthMm,
    heightMm: first.heightMm,
    sameSize,
    matchesTarget: shippingLabelPageSizeMatchesTarget(first),
  };
}

export default function ShippingLabelPdfPanel({ orders = [], workingDeliveryDate = '' }) {
  const { user } = useAuth();
  const printQueue = usePrintQueue();
  const candidateOrders = useMemo(() => {
    const seen = new Set();
    return [...orders, ...printQueue.state.bundles.map((bundle) => bundle.orderSnapshot)]
      .filter(Boolean)
      .filter((order) => {
        const key = String(
          order?.source_id || order?.sourceId || order?.orderId || order?.id || '',
        ).replace(/^#/, '');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [orders, printQueue.state.bundles]);
  const [document, setDocument] = useState(null);
  const [extractedPages, setExtractedPages] = useState([]);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [copies, setCopies] = useState(1);
  const [pageRanges, setPageRanges] = useState('');
  const [pageMatches, setPageMatches] = useState([]);
  const [pageAssignments, setPageAssignments] = useState({});
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  const [qzStatus, setQzStatus] = useState({
    ready: false,
    message: 'Checking QZ Tray…',
    printerDetails: [],
  });
  const [qzSettings, setQzSettings] = useState(() => loadQzPrintSettings());
  const sourceRef = useRef('');
  const autoSuggestionKeysRef = useRef({});
  const loadBlobRef = useRef(null);

  useEffect(() => {
    let active = true;
    getQzPrintReadiness({ force: true }).then((readiness) => {
      if (!active) return;
      setQzStatus(readiness);
      if (readiness.ready) {
        setQzSettings((previous) => {
          const next = reconcileQzPrintSettings(
            previous,
            readiness.printerDetails,
          );
          saveQzPrintSettings(next);
          return next;
        });
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(
    () => () => {
      if (sourceRef.current) deletePrintQueueBlob(sourceRef.current).catch(() => {});
    },
    [],
  );

  useEffect(() => {
    if (!extractedPages.length) return;
    const matches = matchShippingLabelPagesToOrders(extractedPages, candidateOrders, {
      workingDeliveryDate,
    });
    setPageMatches(matches);
    setPageAssignments((previous) => {
      const next = {};
      const nextAuto = {};
      matches.forEach((page) => {
        const prior = previous[page.pageNumber] || '';
        const priorWasAuto =
          !prior || prior === autoSuggestionKeysRef.current[page.pageNumber];
        const suggestion = page.suggestedOrderKey || '';
        next[page.pageNumber] = priorWasAuto ? suggestion : prior;
        nextAuto[page.pageNumber] = suggestion;
      });
      autoSuggestionKeysRef.current = nextAuto;
      return next;
    });
  }, [extractedPages, candidateOrders, workingDeliveryDate]);

  const fileSummary = useMemo(() => {
    if (!document) return '';
    return `${document.pageCount} page${document.pageCount === 1 ? '' : 's'} · ${(
      document.bytes /
      1024 /
      1024
    ).toFixed(2)} MB · ${document.widthMm.toFixed(1)} × ${document.heightMm.toFixed(
      1,
    )} mm`;
  }, [document]);

  const loadBlob = async (blob, name) => {
    setBusy(true);
    setStatus('Inspecting PDF…');
    try {
      const next = await inspectPdf(blob, name);
      const pages = await extractShippingLabelPdfPages(blob);
      const sourceBlobRef = `carrier-source-${globalThis.crypto?.randomUUID?.() || Date.now()}`;
      if (sourceRef.current) await deletePrintQueueBlob(sourceRef.current);
      await putPrintQueueBlob(sourceBlobRef, blob, { temporary: true, name: next.name });
      sourceRef.current = sourceBlobRef;
      autoSuggestionKeysRef.current = {};
      setPageAssignments({});
      setDocument({ ...next, sourceRef: sourceBlobRef });
      setExtractedPages(pages);
      setStatus(
        next.matchesTarget
          ? `Parsed ${next.pageCount} page${next.pageCount === 1 ? '' : 's'} from the file. Review matches, then add to the print queue.`
          : `Parsed ${next.pageCount} page${next.pageCount === 1 ? '' : 's'}, but first-page size differs from 3.94 × 5.91 inches. Review before printing.`,
      );
    } catch (error) {
      setDocument(null);
      setExtractedPages([]);
      setPageMatches([]);
      setPageAssignments({});
      autoSuggestionKeysRef.current = {};
      setStatus(error?.message || 'Unable to load the PDF.');
    } finally {
      setBusy(false);
    }
  };
  loadBlobRef.current = loadBlob;

  useEffect(() => {
    const takeHandoff = () => {
      const handoff = consumeShippingLabelPdfHandoff();
      if (!handoff?.blob) return;
      loadBlobRef.current?.(handoff.blob, handoff.fileName);
    };
    takeHandoff();
    window.addEventListener('lsc:shipping-label-pdf-handoff', takeHandoff);
    return () => window.removeEventListener('lsc:shipping-label-pdf-handoff', takeHandoff);
  }, []);

  const loadFromDriveItems = async (items) => {
    const item = Array.isArray(items) ? items[0] : null;
    if (!item) return;
    setBusy(true);
    setStatus(`Loading “${item.name}” from Smoke Drive…`);
    try {
      const { blob, fileName } = await loadOperatorDriveItemBlob(item, {
        resolveDownloadUrl: async (driveItem) => getDriveShareLink(driveItem.id),
      });
      await loadBlob(blob, fileName);
    } catch (error) {
      setStatus(formatDriveStorageCorsMessage('labels', error));
      setBusy(false);
    }
  };

  const addToQueue = async (startImmediately = false) => {
    if (!document || busy) return;
    setBusy(true);
    setStatus('Splitting and staging vector carrier labels…');
    try {
      const parsedRanges = parsePdfPageRanges(pageRanges, document.pageCount);
      const selectedPages = new Set(parsedRanges.pages);
      const sourceBlob = await getPrintQueueBlob(document.sourceRef);
      if (!sourceBlob) throw new Error('The staged carrier PDF is no longer available.');
      const staged = await stageMatchedShippingLabelPdfToQueue({
        sourceBlob,
        pageMatches,
        pageAssignments,
        candidateOrders,
        workingDeliveryDate,
        copies,
        enqueueCarrierLabel: printQueue.enqueueCarrierLabel,
        selectedPageNumbers: selectedPages,
      });
      await deletePrintQueueBlob(document.sourceRef);
      sourceRef.current = '';
      setDocument(null);
      setExtractedPages([]);
      setPageMatches([]);
      setPageAssignments({});
      autoSuggestionKeysRef.current = {};
      setStatus(
        `${staged.orderCount} order carrier-label bundle${
          staged.orderCount === 1 ? '' : 's'
        } added to the print queue.`,
      );
      if (startImmediately) setTimeout(() => printQueue.startQueue(), 0);
    } catch (error) {
      setStatus(error?.message || 'Shipping-label printing failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="shipping-label-pdf-panel">
      <header>
        <div>
          <h3>Shipping label PDFs</h3>
          <p>
            Provide the carrier PDF file itself (upload or Smoke Drive) — not a download
            link. Each page is parsed and matched to orders. Matching prefers the Pack
            delivery week (OrderDesk folder week ending that Friday) plus the previous and
            next week. Assign pages manually when needed, then stage into the print wave
            queue. The original PDF bytes go to QZ without browser re-rendering.
          </p>
        </div>
        <button
          type="button"
          className="toolkit-btn toolkit-btn--primary"
          disabled={!document || busy}
          onClick={() => addToQueue(false)}
        >
          <PrinterOutlined /> {busy ? 'Working…' : 'Add labels to print queue'}
        </button>
        <button
          type="button"
          className="toolkit-btn toolkit-btn--secondary"
          disabled={!document || busy}
          onClick={() => addToQueue(true)}
        >
          Add and start queue
        </button>
      </header>

      <div className="shipping-label-pdf-panel__sources">
        <label className="shipping-label-upload">
          <UploadOutlined />
          <span>
            <b>Upload PDF</b>
            <small>PDF file only, maximum 50 MB</small>
          </span>
          <input
            type="file"
            accept="application/pdf,.pdf"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) loadBlob(file, file.name);
              event.target.value = '';
            }}
          />
        </label>

        <button
          type="button"
          className="shipping-label-upload shipping-label-upload--button"
          disabled={busy || !user?.uid}
          onClick={() => setDrivePickerOpen(true)}
        >
          <FolderOpenOutlined />
          <span>
            <b>Choose from Smoke Drive</b>
            <small>Loads the library PDF file and parses its pages</small>
          </span>
        </button>
      </div>

      {document && (
        <article
          className={`shipping-label-document${
            document.matchesTarget ? ' is-ready' : ' has-warning'
          }`}
        >
          <div>
            <b>{document.name}</b>
            <small>{fileSummary}</small>
            {!document.sameSize && (
              <small>Warning: not every page uses the same dimensions.</small>
            )}
          </div>
          <label>
            Copies
            <input
              type="number"
              min="1"
              max="99"
              value={copies}
              onChange={(event) =>
                setCopies(Math.max(1, Math.min(99, Number(event.target.value) || 1)))
              }
            />
          </label>
          <label>
            Pages
            <input
              value={pageRanges}
              placeholder="All, or 1-4"
              onChange={(event) => setPageRanges(event.target.value)}
            />
          </label>
        </article>
      )}

      {pageMatches.length > 0 && (
        <section className="shipping-label-page-matches">
          <h4>Match PDF pages to orders</h4>
          <p>Automatic matches must be reviewed. Unmatched or ambiguous pages require a manual order selection.</p>
          {pageMatches.map((page) => (
            <label key={page.pageNumber}>
              <span>
                <b>Page {page.pageNumber}</b>
                <small>{page.status} · {page.textPreview || 'No readable page text'}</small>
                <small className="shipping-label-page-matches__evidence">
                  {page.matchSummary}
                </small>
              </span>
              <select
                value={pageAssignments[page.pageNumber] || ''}
                onChange={(event) =>
                  setPageAssignments((previous) => ({
                    ...previous,
                    [page.pageNumber]: event.target.value,
                  }))
                }
              >
                <option value="">Select order…</option>
                {candidateOrders.map((order) => {
                  const key = String(
                    order?.source_id || order?.sourceId || order?.orderId || order?.id || '',
                  ).replace(/^#/, '');
                  return (
                    <option key={key} value={key}>
                      {getOrderAssignmentLabel(order)}
                    </option>
                  );
                })}
              </select>
            </label>
          ))}
        </section>
      )}

      {status && <p className="shipping-label-pdf-panel__status">{status}</p>}

      <div className="shipping-label-pdf-panel__qz">
        <p>
          <b>{qzStatus.message}</b>
          <br />
          Default: Godex RT730i · Continuous Roll · 3.94 × 5.91 inches.
        </p>
        <button
          type="button"
          className="toolkit-btn toolkit-btn--ghost"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            const readiness = await getQzPrintReadiness({ force: true });
            setQzStatus(readiness);
            setBusy(false);
          }}
        >
          Recheck QZ and printers
        </button>
        {qzStatus.ready && (
          <QzPrinterSettingsPanel
            profileKeys={['shippingLabel']}
            printerDetails={qzStatus.printerDetails || []}
            settings={qzSettings}
            onChange={(next) => {
              setQzSettings(next);
              saveQzPrintSettings(next);
            }}
          />
        )}
      </div>

      <OperatorDrivePicker
        uid={user?.uid}
        open={drivePickerOpen}
        onClose={() => setDrivePickerOpen(false)}
        pdfOnly
        multiple={false}
        title="Choose shipping label PDF"
        hint="The PDF file is loaded from Smoke Drive and parsed here. Download links are not used for printing."
        confirmLabel="Use this PDF"
        onPick={loadFromDriveItems}
      />
    </section>
  );
}
